<template>
    <div class="tw-h-full tw-flex tw-flex-col tw-space-y-3">
        <!-- Progress Indicator -->
        <div class="tw-flex-shrink-0">
            <div class="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-600 tw-mb-2">
                <span>{{ $t('onboarding.progress.step_of', { current: 4, total: 4 }) }}</span>
                <span class="tw-hidden sm:tw-block">{{ $t('onboarding.progress.social_media') }}</span>
                <span class="tw-block sm:tw-hidden">{{ $t('onboarding.progress.social') }}</span>
            </div>
            <div class="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2">
                <div class="tw-bg-primary-600 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300" style="width: 100%"></div>
            </div>
        </div>

        <!-- Scrollable Content -->
        <div class="tw-flex-1 tw-overflow-y-auto tw-space-y-3">
            <!-- Introduction -->
            <div class="tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-lg tw-p-2.5">
                <div class="tw-flex tw-items-start tw-gap-2.5">
                    <icon icon="solar:globus-bold-duotone" class="tw-text-primary-600 tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
                    <div>
                        <h3 class="tw-text-sm tw-font-medium tw-text-primary-800 tw-mb-0.5">
                            {{ $t('onboarding.social_media.title') }}
                        </h3>
                        <p class="tw-text-xs tw-text-primary-700 tw-leading-tight">
                            {{ $t('onboarding.social_media.description') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Social Media Links Grid -->
            <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2.5">
                <!-- LinkedIn -->
                <FormField
                    v-model="formData.linkedin_url"
                    type="url"
                    :label="$t('onboarding.social_media.linkedin')"
                    :placeholder="$t('onboarding.social_media.linkedin_placeholder')"
                    left-icon="ri:linkedin-fill"
                    :error="errors.linkedin_url"
                    :hide-errors="!errors.linkedin_url"
                />

                <!-- Instagram -->
                <FormField
                    v-model="formData.instagram_url"
                    type="url"
                    :label="$t('onboarding.social_media.instagram')"
                    :placeholder="$t('onboarding.social_media.instagram_placeholder')"
                    left-icon="proicons:instagram"
                    :error="errors.instagram_url"
                    :hide-errors="!errors.instagram_url"
                />

                <!-- Twitter -->
                <FormField
                    v-model="formData.twitter_url"
                    type="url"
                    :label="$t('onboarding.social_media.twitter')"
                    :placeholder="$t('onboarding.social_media.twitter_placeholder')"
                    left-icon="hugeicons:new-twitter"
                    :error="errors.twitter_url"
                    :hide-errors="!errors.twitter_url"
                />

                <!-- Facebook -->
                <FormField
                    v-model="formData.facebook_url"
                    type="url"
                    :label="$t('onboarding.social_media.facebook')"
                    :placeholder="$t('onboarding.social_media.facebook_placeholder')"
                    left-icon="ri:facebook-fill"
                    :error="errors.facebook_url"
                    :hide-errors="!errors.facebook_url"
                />

                <!-- YouTube -->
                <FormField
                    v-model="formData.youtube_url"
                    type="url"
                    :label="$t('onboarding.social_media.youtube')"
                    :placeholder="$t('onboarding.social_media.youtube_placeholder')"
                    left-icon="famicons:logo-youtube"
                    :error="errors.youtube_url"
                    :hide-errors="!errors.youtube_url"
                />

                <!-- GitHub -->
                <FormField
                    v-model="formData.github_url"
                    type="url"
                    :label="$t('onboarding.social_media.github')"
                    :placeholder="$t('onboarding.social_media.github_placeholder')"
                    left-icon="mdi:github"
                    :error="errors.github_url"
                    :hide-errors="!errors.github_url"
                />
            </div>

            <!-- Website URL (full width) -->
            <FormField
                v-model="formData.website_url"
                type="url"
                :label="$t('onboarding.social_media.website')"
                :placeholder="$t('onboarding.social_media.website_placeholder')"
                left-icon="solar:earth-bold-duotone"
                :error="errors.website_url"
                :hide-errors="!errors.website_url"
            />

            <!-- Skip Notice -->
            <div class="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-2">
                <div class="tw-flex tw-items-start tw-gap-2">
                    <icon icon="solar:info-circle-bold-duotone" class="tw-text-gray-500 tw-text-xs tw-mt-0.5 tw-flex-shrink-0" />
                    <p class="tw-text-xs tw-text-gray-600 tw-leading-tight">
                        {{ $t('onboarding.social_media.optional_notice') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import FormField from '@/components/form/FormField.vue';

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    errors: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update:formData']);

// Computed form data with two-way binding
const formData = computed({
    get: () => props.formData,
    set: (value) => emit('update:formData', value)
});
</script>

<style scoped>
/* Add any specific styles if needed */
</style> 